import {
    Feature,
    Site,
    User,
} from '@myrtls/api-interfaces';
import { Action, createReducer, on } from '@ngrx/store';

import {
    deleteUserSuccessAction,
    getSiteFailureAction,
    getSiteFeaturesSuccessAction,
    getSiteLicenseRawSuccessAction,
    getSiteRequestAction,
    getSitesFailureAction,
    getSitesRequestAction,
    getSitesSuccessAction,
    getSiteSuccessAction,
    getSiteUsersFailureAction,
    getSiteUsersRequestAction,
    getSiteUsersSuccessAction,
    selectSiteAction,
} from './sites.actions';
import {
    activateDeviceCareSuccessAction,
} from '../monitoring/monitoring.actions';

export interface SiteDetail extends Site {
    users?: User[];
    features?: Feature[];
    licenseRaw?: string;
}

export interface SitesState {
    sites: {
        [siteId: string]: SiteDetail;
    };
    selectedSiteId: string | null;
    loading: boolean;
    loaded: boolean;
}

const initialState: SitesState = {
    selectedSiteId: null,
    loading: false,
    loaded: false,
    sites: {},
};

const reducer = createReducer(
    initialState,
    on(getSitesRequestAction, state => ({
        ...state,
        loading: true,
    })),
    on(getSitesSuccessAction, (state, { sites }) => ({
        ...state,
        loading: false,
        loaded: true,
        sites: {
            ...state.sites,
            ...(sites || []).reduce(
                (acc: { [id: string]: SiteDetail }, site) => ({
                    ...acc,
                    [site.id]: {
                        ...state.sites[site.id],
                        ...site,
                    },
                }),
                {},
            ),
        },
    })),
    on(getSitesFailureAction, state => ({
        ...state,
        loading: false,
        loaded: false,
    })),
    on(getSiteFailureAction, state => ({
        ...state,
        loading: false,
    })),
    on(getSiteRequestAction, state => ({
        ...state,
        loading: true,
    })),
    on(getSiteSuccessAction, (state, { site }) => ({
        ...state,
        loading: false,
        sites: {
            ...state.sites,
            [site.id]: {
                ...state.sites[site.id],
                ...site,
            },
        },
    })),
    on(getSiteUsersRequestAction, state => ({
        ...state,
        loading: true,
    })),
    on(getSiteUsersSuccessAction, (state, { siteId, users }) => ({
        ...state,
        sites: {
            ...state.sites,
            [siteId]: {
                ...state.sites[siteId],
                users,
            },
        },
        loading: false,
    })),
    on(getSiteUsersFailureAction, state => ({
        ...state,
        loading: false,
    })),
    on(selectSiteAction, (state, { siteId }) => ({
        ...state,
        selectedSiteId: siteId,
    })),
    on(deleteUserSuccessAction, (state, { siteId, userId }) => ({
        ...state,
        sites: {
            ...state.sites,
            [siteId]: {
                ...state.sites[siteId],
                users: (state.sites[siteId].users || []).filter(
                    u => u.id !== userId,
                ),
            },
        },
    })),
    on(getSiteFeaturesSuccessAction, (state, { siteId, features }) => ({
        ...state,
        sites: {
            ...state.sites,
            [siteId]: {
                ...state.sites[siteId],
                features,
            },
        },
        loading: false,
    })),
    on(getSiteLicenseRawSuccessAction, (state, { siteId, licenseRaw }) => ({
        ...state,
        sites: {
            ...state.sites,
            [siteId]: {
                ...state.sites[siteId],
                licenseRaw,
            },
        },
        loading: false,
    })),
    on(activateDeviceCareSuccessAction, (state, { date, site }) => ({
        ...state,
        sites: {
            ...state.sites,
            [site]: {
                ...state.sites[site],
                dcActivation: date,
            },
        },
    })),
);

export function sitesReducer(
    state: SitesState | undefined,
    action: Action,
): SitesState {
    return reducer(state, action);
}
